// /pt — Katie's PT micro-internship clinic list. A sub-app of the timtwhit hub (see App.tsx): same
// login cookie, own header, no rowing nav. Cards in Claude's rank order; Katie marks Keep / Cut
// and leaves notes (saved to state.json under clinicEvals).
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import type { Clinic, ClinicStatus, ClinicType, Place } from "../lib/types";
import { StatusControl } from "../components/StatusControl";
import { ClinicMap } from "../components/ClinicMap";

type Filter = "keep" | "cut" | "undecided";
type View = "list" | "map";

const TYPE_BADGE: Record<ClinicType, { label: string; cls: string }> = {
  independent: { label: "Independent", cls: "bg-green-100 text-green-800" },
  "small-group": { label: "Small group", cls: "bg-teal-100 text-teal-800" },
  "hospital-outpatient": { label: "Hospital", cls: "bg-blue-100 text-blue-800" },
  chain: { label: "Chain", cls: "bg-slate-200 text-slate-700" },
};

export default function PtPage() {
  const [clinics, setClinics] = useState<Clinic[] | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Inverted selection (same scheme as the school list, Docs and Quotidian): nothing lit = show
  // every clinic; lighting one or more chips narrows to just those statuses.
  const [filters, setFilters] = useState<Set<Filter>>(new Set());
  const [view, setView] = useState<View>("list");
  const [modalClinicId, setModalClinicId] = useState<string | null>(null);
  // ts makes every click a distinct request even when re-locating the same clinic twice in a
  // row, so ClinicMap's focus effect always fires instead of bailing on an unchanged value.
  const [mapFocus, setMapFocus] = useState<{ lat: number; lon: number; ts: number } | null>(null);

  useEffect(() => {
    api
      .clinics()
      .then(({ clinics, places }) => {
        setClinics(clinics);
        setPlaces(places);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "load failed"));
  }, []);

  const splice = (c: Clinic) =>
    setClinics((prev) => (prev ? prev.map((x) => (x.id === c.id ? c : x)) : prev));

  const counts = useMemo(() => {
    const c = { all: 0, keep: 0, cut: 0, undecided: 0 };
    for (const x of clinics || []) {
      c.all++;
      if (x.evaluation.status === "keep") c.keep++;
      else if (x.evaluation.status === "cut") c.cut++;
      else c.undecided++;
    }
    return c;
  }, [clinics]);

  const shown = useMemo(() => {
    const list = [...(clinics || [])].sort((a, b) => a.claude_rank - b.claude_rank);
    if (!filters.size) return list;
    return list.filter((c) =>
      filters.has(c.evaluation.status === "unranked" ? "undecided" : c.evaluation.status),
    );
  }, [clinics, filters]);

  const toggleFilter = (f: Filter) =>
    setFilters((prev) => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });

  const modalClinic = modalClinicId ? (clinics || []).find((c) => c.id === modalClinicId) : null;

  // "Show on map" button (List card or the marker-click modal) - switches to Map view (a no-op
  // if already there) and hands ClinicMap a fresh focus point to zoom in on.
  function locateOnMap(c: Clinic) {
    setView("map");
    setModalClinicId(null);
    setMapFocus({ lat: c.lat, lon: c.lon, ts: Date.now() });
  }

  const chip = (f: Filter, label: string) => (
    <button
      key={f}
      onClick={() => toggleFilter(f)}
      className={`rounded-full border px-3 py-1 text-sm ${
        filters.has(f)
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
      }`}
    >
      {label} <span className="opacity-70">{counts[f]}</span>
    </button>
  );

  return (
    <div className="mx-auto max-w-3xl p-3 sm:p-4">
      <header className="mb-4">
        <h1 className="text-xl font-bold">Katie's PT Micro-Internship</h1>
        <p className="mt-1 text-sm text-slate-600">
          One week in February 2027 shadowing a physical therapist. Keep the ones that sound
          interesting, cut the ones that don't, and say why.
        </p>
      </header>

      <CallNotes />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {chip("keep", "Keep")}
          {chip("cut", "Cut")}
          {chip("undecided", "Undecided")}
        </div>
        <div className="inline-flex overflow-hidden rounded border border-slate-300">
          <button
            onClick={() => setView("list")}
            className={`px-3 py-1 text-sm ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            List
          </button>
          <button
            onClick={() => setView("map")}
            className={`border-l border-slate-300 px-3 py-1 text-sm ${view === "map" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            Map
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!clinics && !error && <p className="text-sm text-slate-500">Loading…</p>}
      {clinics && shown.length === 0 && (
        <p className="text-sm text-slate-500">Nothing here yet.</p>
      )}

      {clinics && shown.length > 0 && view === "map" && (
        <ClinicMap
          clinics={shown}
          places={places}
          onSelectClinic={setModalClinicId}
          focus={mapFocus}
        />
      )}

      {clinics && view === "list" && (
        <div className="space-y-3">
          {shown.map((c) => (
            <ClinicCard key={c.id} clinic={c} onSaved={splice} onLocate={locateOnMap} />
          ))}
        </div>
      )}

      {modalClinic && (
        <ClinicModal
          clinic={modalClinic}
          onClose={() => setModalClinicId(null)}
          onSaved={splice}
          onLocate={locateOnMap}
        />
      )}
    </div>
  );
}

function ClinicCard({
  clinic: c,
  onSaved,
  onLocate,
}: {
  clinic: Clinic;
  onSaved: (c: Clinic) => void;
  onLocate: (c: Clinic) => void;
}) {
  const [notes, setNotes] = useState(c.evaluation.notes);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!dirty) setNotes(c.evaluation.notes);
  }, [c.evaluation.notes, dirty]);

  async function save(patch: { status?: ClinicStatus; notes?: string }) {
    setSaving(true);
    try {
      const { clinic } = await api.putClinicEval(c.id, patch);
      onSaved(clinic);
      if (patch.notes !== undefined) setDirty(false);
    } finally {
      setSaving(false);
    }
  }

  const badge = TYPE_BADGE[c.type];
  const status = c.evaluation.status;
  const tone =
    status === "keep" ? "border-green-400" : status === "cut" ? "border-red-300 opacity-70" : "";

  return (
    <section className={`card ${tone}`}>
      <div className="flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700"
          title="Claude's rank"
        >
          {c.claude_rank}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold leading-tight">{c.name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
            <span className={`rounded px-1.5 py-0.5 font-medium ${badge.cls}`}>{badge.label}</span>
            <span title="Driving distance from the boathouse">
              ~{c.distance_mi} mi from boathouse · {c.city}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-700">{c.specialty}</p>
        </div>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-800">{c.claude_note}</p>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="text-slate-600">
          {c.address}, {c.city} {c.zip}
        </span>
        <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}>{c.phone}</a>
        <a href={c.website} target="_blank" rel="noreferrer">
          Website
        </a>
        <button className="btn" onClick={() => onLocate(c)}>
          📍 Show on map
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
        <StatusControl
          value={status}
          options={["keep", "cut"]}
          size="md"
          onChange={(s) => void save({ status: s === "maybe" ? "unranked" : s })}
        />
        {saving && <span className="text-xs text-slate-400">Saving…</span>}
      </div>
      <textarea
        className="input mt-2 w-full"
        rows={2}
        placeholder="Why keep or cut? Anything you'd want to ask them?"
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setDirty(true);
        }}
        onBlur={() => {
          if (dirty && notes !== c.evaluation.notes) void save({ notes });
          else setDirty(false);
        }}
      />
    </section>
  );
}

// Opened by clicking a clinic's dot on the Map view - same card the List view renders inline,
// same onSaved/splice wiring, so Keep/Cut and notes stay in sync whichever view made the edit.
function ClinicModal({
  clinic,
  onClose,
  onSaved,
  onLocate,
}: {
  clinic: Clinic;
  onClose: () => void;
  onSaved: (c: Clinic) => void;
  onLocate: (c: Clinic) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      // z-[2000]: Leaflet's own panes/controls (ClinicMap) use z-index up to 1000 and don't sit
      // inside their own stacking context, so they'd otherwise paint over an ordinary z-50 modal.
      className="fixed inset-0 z-[2000] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-2 sm:p-6"
      onClick={onClose}
    >
      <div className="relative w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute -right-2 -top-2 z-10 rounded-full bg-white px-2 py-1 text-slate-400 shadow hover:bg-slate-100 hover:text-slate-700"
        >
          ✕
        </button>
        <ClinicCard clinic={clinic} onSaved={onSaved} onLocate={onLocate} />
      </div>
    </div>
  );
}

function CallNotes() {
  return (
    <details className="card mb-4 text-sm">
      <summary className="cursor-pointer font-semibold">Call notes (for Dad)</summary>
      <div className="mt-3 space-y-3 text-slate-700">
        <div>
          <div className="label">The program</div>
          <p>
            The Mount Vernon School's <strong>Interim</strong> week, February 2027. This year the
            school calls the one-week observation placement a{" "}
            <strong>micro-internship</strong> (grades 10-12; sign-up is a linked form from the
            Interim email, not a Veracross form) — use that word on calls, not "externship" (last
            year's term). The school supplies the agreement / liability / supervisor sign-off
            forms; confirm the exact forms and the Interim dates with the school and note them
            here.{" "}
            <a href="https://mountvernonschool.org/interim/" target="_blank" rel="noreferrer">
              mountvernonschool.org/interim
            </a>
          </p>
        </div>
        <div>
          <div className="label">Criteria</div>
          <p>
            Within ~15 driving miles of 30062. Outpatient orthopedic / sports PT preferred (Katie
            is a rower; it's the setting most used to student shadows). Distances on the cards are
            estimates.
          </p>
        </div>
        <div>
          <div className="label">Who says yes</div>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Independents:</strong> the owner can decide on the spot; PT clinics host
              observation-hours students constantly; a one-week high-schooler is low effort.
              Risk is a small staff bumping the week — get it in writing early.
            </li>
            <li>
              <strong>Hospital outpatient</strong> (Wellstar, CHOA): volunteer/observer office,
              forms, usually an age floor — but used to high-school programs.
            </li>
            <li>
              <strong>Chains</strong> (BenchMark, Physio, ATI…): clinic director → corporate HR,
              formal observer program, often 18+, weeks of turnaround. Fallback only.
            </li>
          </ul>
        </div>
        <div>
          <div className="label">Timeline</div>
          <p>
            First calls by ~November 2026. Commitment + school paperwork by mid-January 2027.
            Interim week: February 2027 (exact dates TBD).
          </p>
        </div>
        <div>
          <div className="label">Script</div>
          <p>
            "Hi — my daughter Katie is a junior at The Mount Vernon School and wants to become a
            physical therapist. Her school runs a one-week micro-internship in February where students
            shadow a professional. She's a competitive rower, so a sports/ortho clinic is exactly
            what she's hoping for. It's observation only, the school provides the agreement and
            liability paperwork, and I'd handle the logistics. Is that something you've done
            before, and could I talk to whoever would decide?"
          </p>
          <p className="mt-2">
            Ask: Do you take student shadows? Any minimum age? Any HIPAA / orientation
            prerequisite? Who would supervise and sign the school's form? Which week(s) in
            February work?
          </p>
        </div>
      </div>
    </details>
  );
}
